import React, { useState, useEffect } from 'react';
import { Box, Button, TextField, Paper, Dialog, DialogTitle, 
    DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem, Alert } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { apiGet, apiRequest } from '../../../utils/fetch';
import { UserRoleInterface, GenericResponseInterface } from '../../../interfaces/interfaces';
import { getUserRoleTableColumns } from './UserRolesTableColumns';

const UserRolesAdminList: React.FC = () => {
    const [roles, setRoles] = useState<UserRoleInterface[]>([]);
    const [role, setRole] = useState<UserRoleInterface | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [roleId, setRoleId] = useState('');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [roleToDelete, setRoleToDelete] = useState<UserRoleInterface | null>(null);
    const [toggleActiveDialogOpen, setToggleActiveDialogOpen] = useState(false);
    const [roleToToggleActive, setRoleToToggleActive] = useState<UserRoleInterface | null>(null);
    const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
    const [roleToUpdate, setRoleToUpdate] = useState<UserRoleInterface | null>(null);
    const [updateForm, setUpdateForm] = useState<Partial<UserRoleInterface>>({});
    const [updateError, setUpdateError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);    

    // Fetch all roles
    const handleGetAllRoles = async () => {
        setError(null);
        setRole(null);
        setSuccess(null);
        try {
            const result = await apiGet<GenericResponseInterface<UserRoleInterface[]>>('/userRoles');
            if (result.status && result.data) {
                setRoles(result.data);
            } else {
                setError(result.message || 'Failed to fetch roles');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to fetch roles');
        }
    };

    // Fetch role by id
    const handleGetRoleById = async () => {
        setError(null);
        setRoles([]);
        setSuccess(null);
        if (!roleId) return setError('Please enter a role ID');
        try {
            const result = await apiGet<GenericResponseInterface<UserRoleInterface>>(`/userRoles/${roleId}`);
            if (result.status && result.data) {
                setRole(result.data);
            } else {
                setError(result.message || 'Failed to fetch role');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to fetch role');
        }
    };

    // Delete role
    const handleDeleteRole = async () => {
        if (!roleToDelete) return;
        setError(null);
        setSuccess(null);
        try {
            const result = await apiRequest<GenericResponseInterface>(`/userRoles/delete/${roleToDelete.id}`, 'DELETE');
            if (result.status) {
                setSuccess(result.message || 'Role deleted successfully');
                setDeleteDialogOpen(false);
                setRoleToDelete(null);
                handleGetAllRoles();
            } else {
                setError(result.message || 'Failed to delete role');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to delete role');
        }
    };

    // Toggle active
    const handleToggleActive = async () => {
        if (!roleToToggleActive) return;
        setError(null);
        setSuccess(null);
        try {
            const result = await apiRequest<GenericResponseInterface>(`/userRoles/toggleActive/${roleToToggleActive.id}`, 'PUT');
            if (result.status) {
                setSuccess(result.message || 'Active status toggled successfully');
                setToggleActiveDialogOpen(false);
                setRoleToToggleActive(null);
                handleGetAllRoles();
            } else {
                setError(result.message || 'Failed to toggle active status');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to toggle active status');
        }
    };

    const handleUpdateRole = async () => {
        if (!roleToUpdate || !roleToUpdate.id) return;
        setUpdateError(null);
        setSuccess(null);
        try {
            const result = await apiRequest<GenericResponseInterface<UserRoleInterface>>(
                `/userRoles/update/${roleToUpdate.id}`,
                'PUT',
                {
                    ...roleToUpdate,
                    ...updateForm,
                }
            );
            if (result.status) {
                setSuccess(result.message || 'Role updated successfully');
                setUpdateDialogOpen(false);
                setRoleToUpdate(null);
                setUpdateForm({});
                handleGetAllRoles();
            } else {
                setUpdateError(result.message || 'Failed to update role');
            }
        } catch (err: any) {
            setUpdateError(err.message || 'Failed to update role');
        }
    };    

    const columns = getUserRoleTableColumns(
        setRoleToDelete,
        setDeleteDialogOpen,
        setRoleToToggleActive,
        setToggleActiveDialogOpen,
        setRoleToUpdate,
        setUpdateDialogOpen        
    );

    useEffect(() => {
        handleGetAllRoles();
    }, []);

    useEffect(() => {
    if (updateDialogOpen && roleToUpdate) {
        setUpdateForm({
        roleName: roleToUpdate.roleName,
        description: roleToUpdate.description,
        });
    }
    }, [updateDialogOpen, roleToUpdate]);    

    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Button variant="contained" onClick={handleGetAllRoles}>Get All Roles</Button>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Button variant="contained" onClick={handleGetRoleById}>Get Role by ID</Button>
                <TextField
                    label="Role ID"
                    value={roleId}
                    onChange={e => setRoleId(e.target.value)}
                    size="small"
                />
            </Box>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
            <Paper sx={{ width: '100%', overflow: 'auto', mt: 2 }}>
                {roles.length > 0 && (
                    <DataGrid
                        rows={roles.map(r => ({ ...r, id: r.id ?? Math.random() }))}
                        columns={columns}
                        initialState={{
                            pagination: { paginationModel: { pageSize: 10, page: 0 } }
                        }}
                        pageSizeOptions={[10, 25, 50]}
                        autoHeight
                    />
                )}
                {role && (
                    <DataGrid
                        rows={[{ ...role, id: role.id ?? Math.random() }]}
                        columns={columns}
                        hideFooter
                        autoHeight
                    />
                )}
            </Paper>

            {/* Delete confirmation dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    Are you sure you want to delete this role?
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button color="error" onClick={handleDeleteRole}>Delete</Button>
                </DialogActions>
            </Dialog>

            {/* Toggle active dialog */}
            <Dialog open={toggleActiveDialogOpen} onClose={() => setToggleActiveDialogOpen(false)}>
                <DialogTitle>Toggle Active</DialogTitle>
                <DialogContent>
                    Are you sure you want to toggle the active status on this role?
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setToggleActiveDialogOpen(false)}>Cancel</Button>
                    <Button color="warning" onClick={handleToggleActive}>Toggle</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={updateDialogOpen} onClose={() => {
                setUpdateDialogOpen(false);
                setSuccess(null);
            }}>
                <DialogTitle>Update Role</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Role Name"
                        value={updateForm.roleName ?? roleToUpdate?.roleName ?? ''}
                        onChange={e => setUpdateForm(f => ({ ...f, roleName: e.target.value }))}
                        fullWidth
                    margin="normal"
                    />
                    <TextField
                        label="Description"
                        value={updateForm.description ?? roleToUpdate?.description ?? ''}
                        onChange={e => setUpdateForm(f => ({ ...f, description: e.target.value }))}
                        fullWidth
                        margin="normal"
                    />

                    <FormControl fullWidth margin="normal">
                        <InputLabel id="active-label">Active</InputLabel>
                        <Select
                            labelId="active-label"
                            value={
                                updateForm.active !== undefined
                                    ? updateForm.active
                                        ? 'yes'
                                        : 'no'
                                    : roleToUpdate?.active
                                        ? 'yes'
                                        : 'no'
                            }
                            label="Active"
                            onChange={e =>
                                setUpdateForm(f => ({
                                    ...f,
                                    active: e.target.value === 'yes'
                                }))
                            }
                        >
                            <MenuItem value="yes">Yes</MenuItem>
                            <MenuItem value="no">No</MenuItem>
                        </Select>
                    </FormControl>
                    {updateError && <Alert severity="error" sx={{ mt: 2 }}>{updateError}</Alert>}
                    {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUpdateDialogOpen(false)}>Cancel</Button>
                    <Button
                        color="primary"
                        onClick={handleUpdateRole}
                    >
                        Update
                    </Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
};

export default UserRolesAdminList;